import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Gift, Copy, Check, Users, Coins, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import api from '../utils/api';
import { toast } from '../hooks/use-toast';

const ReferralPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [applyCode, setApplyCode] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    api.get('/referral/stats').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const copyCode = () => {
    if (!data?.code) return;
    const url = `${window.location.origin}?ref=${data.code}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast({ title: 'Referral link copied!', description: 'Share it with friends to earn credits.' });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleApply = async () => {
    if (!applyCode.trim()) return;
    setApplying(true);
    try {
      const res = await api.post('/referral/apply', { code: applyCode.trim() });
      toast({ title: 'Referral applied!', description: res.data.message });
      // Refresh stats
      const updated = await api.get('/referral/stats');
      setData(updated.data);
      setApplyCode('');
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Invalid code', variant: 'destructive' });
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="referral-page">
      <div className="bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-2 text-foreground/80 hover:text-foreground hover:bg-accent" data-testid="referral-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <Gift className="w-7 h-7 text-amber-400" />
            <div>
              <h1 className="text-3xl font-bold text-foreground">Invite Friends</h1>
              <p className="text-muted-foreground mt-0.5">Earn {data?.bonusPerReferral || 50} credits for every friend who joins</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* How it works */}
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-2xl p-6">
          <h3 className="text-foreground font-semibold mb-3">How it works</h3>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { step: '1', title: 'Share your link', desc: 'Copy your unique referral link below' },
              { step: '2', title: 'Friend signs up', desc: 'They create an account using your link' },
              { step: '3', title: 'Both earn credits', desc: `You both get ${data?.bonusPerReferral || 50} bonus credits` }
            ].map(s => (
              <div key={s.step} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-sm font-bold flex-shrink-0">{s.step}</div>
                <div>
                  <p className="text-white text-sm font-medium">{s.title}</p>
                  <p className="text-muted-foreground text-xs mt-0.5">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-blue-500/10 border-blue-500/20 border">
            <CardContent className="p-5 text-center">
              <Users className="w-8 h-8 text-blue-400 mx-auto" />
              <div className="text-2xl font-bold text-blue-400 mt-2">{data?.referralCount || 0}</div>
              <div className="text-muted-foreground text-sm">Friends Invited</div>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/10 border-emerald-500/20 border">
            <CardContent className="p-5 text-center">
              <Coins className="w-8 h-8 text-emerald-400 mx-auto" />
              <div className="text-2xl font-bold text-emerald-400 mt-2">{data?.creditsEarned || 0}</div>
              <div className="text-muted-foreground text-sm">Credits Earned</div>
            </CardContent>
          </Card>
          <Card className="bg-purple-500/10 border-purple-500/20 border">
            <CardContent className="p-5 text-center">
              <Gift className="w-8 h-8 text-purple-400 mx-auto" />
              <div className="text-2xl font-bold text-purple-400 mt-2">{data?.bonusPerReferral || 50}</div>
              <div className="text-muted-foreground text-sm">Per Referral</div>
            </CardContent>
          </Card>
        </div>

        {/* Your Referral Link */}
        <Card className="bg-card border-border">
          <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><Share2 className="w-5 h-5 text-blue-400" /> Your Referral Link</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input
                readOnly
                value={data?.code ? `${window.location.origin}?ref=${data.code}` : 'Loading...'}
                className="bg-muted border-input text-white font-mono"
                data-testid="referral-link-input"
              />
              <Button onClick={copyCode} className="bg-blue-600 hover:bg-blue-500 text-white px-6" data-testid="copy-referral-btn">
                {copied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy</>}
              </Button>
            </div>
            <p className="text-muted-foreground text-xs mt-2">Your code: <span className="text-white font-mono">{data?.code || '...'}</span></p>
          </CardContent>
        </Card>

        {/* Apply Referral Code */}
        <Card className="bg-card border-border">
          <CardHeader><CardTitle className="text-white text-lg">Have a referral code?</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input
                placeholder="Enter referral code (e.g. A1B2C3D4)"
                value={applyCode}
                onChange={e => setApplyCode(e.target.value)}
                className="bg-muted border-input text-white"
                data-testid="apply-code-input"
              />
              <Button onClick={handleApply} disabled={applying || !applyCode.trim()} className="bg-emerald-600 hover:bg-emerald-500 text-white px-6" data-testid="apply-code-btn">
                {applying ? 'Applying...' : 'Apply'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Referral History */}
        {data?.referrals?.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader><CardTitle className="text-white text-lg">Referral History</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.referrals.map((r, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-3 bg-muted rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Users className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div>
                        <span className="text-white text-sm font-medium">{r.referredName}</span>
                        <span className="text-muted-foreground text-xs ml-2">{r.referredEmail}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-emerald-400 text-sm font-bold">+{r.credits_awarded} credits</span>
                      <div className="text-muted-foreground text-xs">{new Date(r.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReferralPage;
